import styled from 'styled-components';
import { ComponentType, PropsWithChildren, useMemo, useState } from 'react';
import {
  BasicFilterSelect,
  ALIGNMENT,
  SelectValueType,
  SelectValueDisplayProps,
  BasicOptionProps,
} from 'ui/selects';
import { useMissionControlContext } from 'modules/missionControl/contexts/MissionControlContext';
import { BUTTON_SIZES, GhostButton } from 'ui/button';
import { ModalType, useModalContext } from 'modules/missionControl/contexts/ModalContext';
import {
  PlaceholderDescription,
  PlaceholderState,
  PlaceholderTitle,
} from 'ui/indexPages/PlaceholderState';
import { pxToRem } from 'utils/convert';
import { AddGoalSelectOption } from './AddGoalSelectOption';
import { EmptyStateGoals } from 'ui/illustrations/EmptyStateGoals';
import { body2, subtitle1 } from 'ui/theme/typography';
import { useCanCreateGoals } from 'modules/missionControl/hooks/useCanCreateGoals';
import { useTranslation } from 'modules/queryBuilder/hooks/useTranslation';
import { GoalEventType } from 'modules/goals/types';

export const ADD_GOAL_SELECT_TEST_ID = 'add-goal-select';

const FooterButton = styled(GhostButton)`
  width: 100%;
`;

const StyledPlaceholderState = styled(PlaceholderState)`
  min-height: ${pxToRem(210)};
  &${PlaceholderTitle} {
    ${subtitle1}
  }
  &${PlaceholderDescription} {
    ${body2}
  }
`;

const SelectWrapper = styled.div`
  /* this is used to override the default styles of the select wrapper */
`;

const StyledSelect = styled(BasicFilterSelect)`
  flex-shrink: 0;
  && #trigger-${ADD_GOAL_SELECT_TEST_ID} {
    border: unset; /** Removes border from the trigger wrapper */
  }
`;

type AddGoalSelectProps = {
  verticalAlignment?: ALIGNMENT;
  horizontalAlignment?: ALIGNMENT;
  onSelect: (goalIds: string[]) => void;
  Trigger?: ComponentType<PropsWithChildren<SelectValueDisplayProps<SelectValueType>>>;
};

export const AddGoalSelect = ({
  onSelect,
  Trigger,
  verticalAlignment = ALIGNMENT.DOWN,
  horizontalAlignment = ALIGNMENT.RIGHT,
}: AddGoalSelectProps) => {
  const {
    projectGoalsQuery: { projectGoals, isLoading: isLoadingProjectGoals },
    missionControlUserPreferencesQuery: {
      userPreferences,
      isLoading: isLoadingMissionControlUserPreferences,
    },
  } = useMissionControlContext();

  const { t } = useTranslation();
  const canCreateGoals = useCanCreateGoals();
  const [search, setSearch] = useState('');
  const { openModal, closeModal } = useModalContext();

  const options = useMemo(() => {
    return projectGoals
      ?.filter((goal) => !userPreferences?.subscribedGoalIds?.includes(goal.id))
      .map((goal) => ({
        id: goal.id.toString(),
        label: goal.name,
        value: goal.id.toString(),
        data: goal,
      }));
  }, [projectGoals, userPreferences]);

  const filteredOptions = useMemo(
    () =>
      options.filter((option) => {
        const systemEventName =
          t(`v2.requirementBuilder.eventName.${option.data?.eventName}`) || option.data?.eventName;

        const eventName =
          option.data?.eventType === GoalEventType.System
            ? systemEventName
            : option.data?.eventName;

        return (
          !search ||
          option.label.toLowerCase().includes(search.toLowerCase()) ||
          eventName?.toLowerCase().includes(search.toLowerCase())
        );
      }),
    [options, search, t],
  );

  const createGoal = () =>
    openModal(ModalType.CreateGoalModal, {
      onClose: () => closeModal(ModalType.CreateGoalModal),
    });

  const handleSelect = (values: string[]) => {
    onSelect(values);
  };
  const handleSearchChange = (search: string | undefined) => setSearch(search || '');

  return (
    <StyledSelect
      showAllCategories
      disableInternalFiltering
      automaticallyAvoidCollisions
      id={ADD_GOAL_SELECT_TEST_ID}
      shouldDisableOnEmpty={false}
      width="480px"
      emptyStateText="No goals found"
      options={filteredOptions}
      isLoading={isLoadingProjectGoals || isLoadingMissionControlUserPreferences}
      horizontalAlignment={horizontalAlignment}
      verticalAlignment={verticalAlignment}
      Trigger={Trigger}
      Option={
        AddGoalSelectOption as ComponentType<PropsWithChildren<BasicOptionProps<SelectValueType>>>
      }
      SelectWrapper={SelectWrapper}
      onSearchChange={handleSearchChange}
      EmptyState={() => (
        <StyledPlaceholderState Illustration={EmptyStateGoals} title="No available goals to add">
          All goals have already been added to your homepage.
        </StyledPlaceholderState>
      )}
      footerButtonContent={
        canCreateGoals ? (
          <FooterButton size={BUTTON_SIZES.MEDIUM} onClick={createGoal}>
            Create new goal
          </FooterButton>
        ) : undefined
      }
      inputContent={{
        filterPlaceholder: 'Search by goal name or event',
      }}
      onSelect={(values) => {
        handleSelect(values as string[]);
      }}
    />
  );
};
